import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { TGFormControlQuestionDirective } from '../form-control-question';

@Component({
  selector: 'tgf-form-dropdown-question',
  templateUrl: './form-dropdown-question.component.html',
  styleUrls: ['./form-dropdown-question.component.scss']
})
export class FormDropdownQuestionComponent extends TGFormControlQuestionDirective {
  @Input() placeholder: string = 'Please Select...';
  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('optionsContainer') optionsContainer!: ElementRef;

  isOpen = false;
  dropdownTop = 0;
  dropdownLeft = 0;
  dropdownWidth = 0;

  focusedOptionIndex = -1;

  searchText: string = '';

  getSelectedText() {
    const selected = this.questionModel['choices']
      .find((selectedOption: any) => selectedOption.value === this.control?.value);
    return selected?.label || '';
  }

  toggleDropdown(shouldOnlyClose?: boolean) {
    this.isOpen = shouldOnlyClose ? false : !this.isOpen;
    if (this.isOpen) {
      this.updateDropdownPosition();
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus();
        }
        this.scrollToSelectedOption();
      });
    } else {
      this.searchText = '';
    }
    this.changeDetectorRef.detectChanges();
  }

  scrollToSelectedOption() {
    if (!this.optionsContainer || !this.questionModel.value) return;

    setTimeout(() => {
      const options = this.getOptions();
      const selectedIndex = options.findIndex((option: any) =>
        option.value === this.questionModel.value
      );

      if (selectedIndex > -1 && this.optionsContainer.nativeElement.children[selectedIndex]) {
        this.scrollToOptionByIndex(selectedIndex);
        this.focusedOptionIndex = selectedIndex;
        this.changeDetectorRef.detectChanges();
      }
    }, 0);
  }

  filterOptions() {
    this.focusedOptionIndex = -1;
    if (!this.isOpen) {
      this.toggleDropdown();
    }
    this.changeDetectorRef.detectChanges();
  }

  updateDropdownPosition() {
    const container = document.querySelector('#tg-sv-dropdown') as HTMLElement;
    if (container) {
      const rect = container.getBoundingClientRect();
      this.dropdownTop = rect.height;
      this.dropdownLeft = 0;
      this.dropdownWidth = rect.width;
    }
  }

  selectOption(value: any, restartSearch: boolean = true) {
    this.questionModel.value = value;

    this.setFormControlValue(value);
    if (restartSearch) {
      this.searchText = '';
    }
    const options = this.getOptions();
    this.focusedOptionIndex = options.findIndex((option: any) => option.value === this.questionModel.value);
  }

  setFormControlValue(value: any) {
    this.form.get(this.questionModel.controlName)?.setValue(value);
    this.form.get(this.questionModel.controlName)?.markAsTouched();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (this.isOpen) {
      if (!this.dropdownContainer.nativeElement.contains(event.target as HTMLElement)) {
        this.toggleDropdown(true);
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  getOptions() {
    return this.questionModel.choices ? this.searchText ?
      this.questionModel.choices.filter((option: any) => {
        return option.label &&
          option.label.toLowerCase().includes(this.searchText.trim().toLowerCase());
      }) :
      this.questionModel.choices : [];
  }

  moveSelection(direction: number) {
    const options = this.getOptions();
    if (options.length === 0) return;

    if (this.focusedOptionIndex === -1) {
      const currentValueIndex = options.findIndex((option: any) => option.value === this.questionModel.value);
      this.focusedOptionIndex = currentValueIndex >= 0 ? currentValueIndex :
        (direction > 0 ? 0 : options.length - 1);
    } else {
      const newIndex = this.focusedOptionIndex + direction;
      if (newIndex >= 0 && newIndex < options.length) {
        this.focusedOptionIndex = newIndex;
      }
    }

    if (this.optionsContainer && this.optionsContainer.nativeElement.children[this.focusedOptionIndex]) {
      this.scrollToOptionByIndex(this.focusedOptionIndex);
    }

    this.changeDetectorRef.detectChanges();
  }

  private scrollToOptionByIndex(index: number) {
    this.optionsContainer.nativeElement.children[index].scrollIntoView({
      block: 'nearest',
      behavior: 'auto'
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isOpen) {
      this.processKeyDownEvent(event);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
      this.toggleDropdown();
      event.preventDefault();
    }
  }


  private processKeyDownEvent(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        this.moveSelection(1);
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.moveSelection(-1);
        event.preventDefault();
        break;
      case 'Enter':
        this.selectFocusedOption();
        event.preventDefault();
        break;
      case 'Escape':
        this.toggleDropdown(true);
        event.preventDefault();
        break;
    }
  }

  selectFocusedOption() {
    const options = this.getOptions();
    if (this.focusedOptionIndex >= 0 && this.focusedOptionIndex < options.length) {
      this.selectOption(options[this.focusedOptionIndex].value);
      this.toggleDropdown(true);
    }
  }

}
